import React, { useCallback, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import DailyReportFilterDrawer from './DailyReportFilterDrawer'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import ExportExcel from '../../../../../components/Export'
import ProductGroupSelectSearch from '../../../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../../../components/SelectSearch/ChannelSelectSearch'
import FilterShippings from '../../../../../components/FiltersShippings'
import { EOrderDateType, EOrderStatus } from '../../../../../types/order'

export interface IFilterMapped {
  dateType?: EOrderDateType
  categories?: string[]
  products?: string[]
  productGroups?: string[]
  distribution?: string[]
  startDate?: string
  endDate?: string
  trackingProvider?: string
  providerStatus?: string
  spacificStatus?: string[]
}

interface FilterProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  onDownload?: (date: Dayjs) => void
}

const DailyReportFilter = (props: FilterProps) => {
  const { onFilterChange, filter, onSearch, onDownload } = props
  const { startDate, endDate } = filter
  const [isOpenedFilterDrawer, setIsOpenedFilterDrawer] =
    useState<boolean>(false)

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  return (
    <>
      <DailyReportFilterDrawer
        isOpen={isOpenedFilterDrawer}
        onClose={() => setIsOpenedFilterDrawer(false)}
        onFilterChange={onFilterChange}
        filter={filter}
        onSearch={onSearch}
      />
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} lg={2}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="dateType-label">ค้นหาจากวันที่</InputLabel>
            <Select
              id="dateType"
              name="dateType"
              labelId="dateType-label"
              value={filter.dateType ?? EOrderDateType.ORDERDATE}
              onChange={(e) =>
                handleFilterChange({
                  dateType: e.target.value as EOrderDateType,
                })
              }
              fullWidth
            >
              <MenuItem value={EOrderDateType.ORDERDATE}>
                วันที่คำสั่งซื้อ
              </MenuItem>
              <MenuItem value={EOrderDateType.CREATED}>
                วันที่สร้างคำสั่งซื้อ
              </MenuItem>
              <MenuItem value={EOrderDateType.PICKUP}>
                วันที่ขนส่งรับสินค้า
              </MenuItem>
              <MenuItem value={EOrderDateType.PRINTED}>วันที่พิมพ์</MenuItem>
              <MenuItem value={EOrderDateType.TRACKING}>
                วันที่สถานะเลขพัสดุ
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={ONLY_DATE_FORMAT}
              // disableFuture
              label="เลือกวันที่เริ่มต้น"
              value={startDate ? dayjs(startDate) : null}
              onChange={(e) =>
                onFilterChange({ ...filter, startDate: e!.format() })
              }
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={ONLY_DATE_FORMAT}
              // disableFuture
              label="เลือกวันที่สิ้นสุด"
              value={endDate ? dayjs(endDate) : null}
              onChange={(e) =>
                onFilterChange({ ...filter, endDate: e!.format() })
              }
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} lg={3}>
          <CategorySelectSearch
            categories={filter.categories}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category: any) => category?.value?.objectId,
              )
              onFilterChange({ ...filter, categories: selectedCategoryIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ProductSelectSearch
            // resetAutoComplete={resetAutoComplete}
            products={filter.products}
            handleFilterChange={onFilterChange}
            onSelect={(e) => {
              const selectedProductIds = e?.map(
                (product: any) => product?.value?.objectId,
              )
              onFilterChange({ ...filter, products: selectedProductIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ProductGroupSelectSearch
            productGroups={filter.productGroups}
            onSelect={(e) => {
              const selectedGroupIds = e?.map((group) => group.value.objectId)
              onFilterChange({ ...filter, productGroups: selectedGroupIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ChannelSelectSearch
            label={'เลือกช่องทางการขาย'}
            onSelect={(e: any) => {
              const selectedDistributionIds = e?.map(
                (distribution: any) => distribution?.value?.objectId,
              )
              onFilterChange({
                ...filter,
                distribution: selectedDistributionIds,
              })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <TextField
            label="สถานะ"
            value={filter.spacificStatus?.[0] ?? 'all'}
            onChange={(e) => {
              const d = e.target.value
              if (d) {
                handleFilterChange({
                  spacificStatus: [e.target.value],
                  trackingProvider: undefined,
                  providerStatus: undefined,
                })
              } else {
                handleFilterChange({
                  spacificStatus: undefined,
                  trackingProvider: undefined,
                  providerStatus: undefined,
                })
              }
            }}
            fullWidth
            select
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            <MenuItem value={EOrderStatus.PENDING}>รอส่ง</MenuItem>
            <MenuItem value={EOrderStatus.TRACKING}>สร้างเลขพัสดุแล้ว</MenuItem>
            <MenuItem value={EOrderStatus.TRANSIT}>กำลังขนส่ง</MenuItem>
            <MenuItem value={EOrderStatus.DELIVERED}>ส่งสำเร็จ</MenuItem>
            <MenuItem value={EOrderStatus.SUCCESS}>ส่งแล้ว (กำหนดเอง)</MenuItem>
            <MenuItem value={EOrderStatus.CANCLELLED}>ยกเลิก</MenuItem>
            <MenuItem value={EOrderStatus.RETURN}>ตีกลับ</MenuItem>
            <MenuItem value={EOrderStatus.RETURNED}>
              ตีกลับ (รับสินค้าแล้ว)
            </MenuItem>
          </TextField>
        </Grid>
        {(filter.spacificStatus?.includes(EOrderStatus.TRANSIT) ||
          filter.spacificStatus?.includes(EOrderStatus.RETURN) ||
          filter.spacificStatus?.includes(EOrderStatus.DELIVERED) ||
          filter.spacificStatus?.includes(EOrderStatus.SUCCESS) ||
          filter.spacificStatus?.includes(EOrderStatus.RETURNED)) && (
          <Grid item xs={12} lg={8}>
            <FilterShippings
              onFilterChange={handleFilterChange}
              filter={filter}
            />
          </Grid>
        )}
        {/* <Grid item xs={12} lg={2}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<FilterAltIcon />}
            onClick={() => {
              setIsOpenedFilterDrawer(true)
            }}
            sx={{ height: '100%' }}
          >
            ค้นหาโดยละเอียด
          </Button>
        </Grid> */}
        <Grid item xs={12} lg={2}>
          <Button
            color="primary"
            size={'large'}
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={12} lg={2}>
          <ExportExcel onDownload={onDownload} filterDate={false} />
        </Grid>
      </Grid>
    </>
  )
}

export default DailyReportFilter
